<template>
	<v-navigation-drawer
		id="app-drawer"
		app
		mini-variant
		permanent
		mini-variant-width="72"
	>
		<div class="drawer__brand" @click="pushRoute('dashboard')">
			<v-img
				:src="brandLogo"
				height="40"
				width="40"
				contain
				class="drawer__brand-logo"
			/>
		</div>

		<div class="drawer__divider"></div>

		<div v-if="!getDrawer && favoriteForms.length > 0" class="drawer__favorites">
			<div class="drawer__settings-label">
				{{ $t("drawer.favorites") }}
			</div>
			<v-list nav dense class="drawer__list drawer__list--settings">
				<v-list-item
					v-for="fav in favoriteForms"
					:key="`fav-${fav.ID}`"
					link
					class="drawer__item"
					:class="{ 'drawer__item--active': isItemActive(fav) }"
					@click="pushRoute(fav.link)"
				>
					<span class="drawer__item-indicator"></span>
					<v-icon size="18" class="drawer__item-icon">{{ fav.icon }}</v-icon>
					<v-list-item-content>
						<v-list-item-title class="drawer__item-title">
							{{ fav.title }}
						</v-list-item-title>
					</v-list-item-content>
					<v-btn
						icon
						x-small
						class="drawer__item-star drawer__item-star--on"
						@click.stop="toggleFavorite(fav)"
					>
						<v-icon size="16">mdi-star</v-icon>
					</v-btn>
				</v-list-item>
			</v-list>
			<div class="drawer__divider"></div>
		</div>

		<div v-if="!getDrawer" class="drawer__search">
			<v-text-field
				v-model="menuSearch"
				prepend-inner-icon="mdi-magnify"
				:placeholder="$t('drawer.searchPlaceholder')"
				solo
				flat
				dense
				hide-details
				clearable
				background-color="transparent"
				class="drawer__search-input"
			/>
		</div>

		<v-list nav dense class="drawer__list">
			<div
				v-if="!getDrawer && hasSearchQuery && visibleMainRoots.length === 0"
				class="drawer__empty"
			>
				{{ $t("drawer.searchEmpty") }}
			</div>
			<template v-for="root in visibleMainRoots">
				<v-menu
					v-if="getDrawer"
					:key="`mini-${root.ID}`"
					open-on-hover
					offset-x
					right
					:close-delay="120"
					content-class="drawer__popout-menu"
					:disabled="!hasChildrenOrItems(root)"
				>
					<template v-slot:activator="{ on }">
						<v-list-item
							link
							class="drawer__mini-root"
							:class="{ 'drawer__mini-root--active': isRootActive(root) }"
							@click="navigateToGroup(root)"
							v-on="on"
						>
							<v-icon size="22">{{ root.icon }}</v-icon>
						</v-list-item>
					</template>

					<div class="drawer__popout">
						<div class="drawer__popout-header">
							<v-icon size="18" class="drawer__popout-header-icon">{{ root.icon }}</v-icon>
							<span class="drawer__popout-header-title">{{ root.title }}</span>
						</div>

						<div
							v-if="root.items && root.items.length > 0"
							class="drawer__popout-section"
						>
							<button
								v-for="item in root.items"
								:key="`popitem-${item.ID}`"
								type="button"
								class="drawer__popout-item"
								:class="{ 'drawer__popout-item--active': isItemActive(item) }"
								@click="pushRoute(item.link)"
							>
								<v-icon size="16">{{ item.icon }}</v-icon>
								<span>{{ item.title }}</span>
							</button>
						</div>

						<div
							v-for="sub in root.children"
							:key="`popsub-${sub.ID}`"
							class="drawer__popout-section"
						>
							<div class="drawer__popout-section-title">{{ sub.title }}</div>
							<button
								v-for="item in sub.items"
								:key="`popsubitem-${item.ID}`"
								type="button"
								class="drawer__popout-item"
								:class="{ 'drawer__popout-item--active': isItemActive(item) }"
								@click="pushRoute(item.link)"
							>
								<v-icon size="16">{{ item.icon }}</v-icon>
								<span>{{ item.title }}</span>
							</button>
						</div>
					</div>
				</v-menu>

				<v-list-group
					v-else
					:key="`grp-${root.ID}`"
					no-action
					:value="hasSearchQuery || isRootActive(root)"
					class="drawer__group"
				>
					<template v-slot:activator>
						<v-list-item-icon class="drawer__group-icon">
							<v-icon size="20">{{ root.icon }}</v-icon>
						</v-list-item-icon>
						<v-list-item-content>
							<v-list-item-title class="drawer__group-title">
								{{ root.title }}
							</v-list-item-title>
						</v-list-item-content>
					</template>

					<v-list-item
						v-for="item in root.items"
						:key="`item-${item.ID}`"
						link
						class="drawer__item"
						:class="{ 'drawer__item--active': isItemActive(item) }"
						@click="pushRoute(item.link)"
					>
						<span class="drawer__item-indicator"></span>
						<v-icon size="18" class="drawer__item-icon">{{ item.icon }}</v-icon>
						<v-list-item-content>
							<v-list-item-title class="drawer__item-title">
								{{ item.title }}
							</v-list-item-title>
						</v-list-item-content>
						<v-btn
							icon
							x-small
							class="drawer__item-star"
							:class="{ 'drawer__item-star--on': isFavorite(item.ID) }"
							@click.stop="toggleFavorite(item)"
						>
							<v-icon size="16">
								{{ isFavorite(item.ID) ? "mdi-star" : "mdi-star-outline" }}
							</v-icon>
						</v-btn>
					</v-list-item>

					<v-list-group
						v-for="sub in root.children"
						:key="`sub-${sub.ID}`"
						no-action
						sub-group
						:value="hasSearchQuery || isSubGroupActive(sub)"
						class="drawer__subgroup"
					>
						<template v-slot:activator>
							<v-list-item-icon class="drawer__subgroup-icon">
								<v-icon size="18">{{ sub.icon }}</v-icon>
							</v-list-item-icon>
							<v-list-item-content>
								<v-list-item-title class="drawer__subgroup-title">
									{{ sub.title }}
								</v-list-item-title>
							</v-list-item-content>
						</template>

						<v-list-item
							v-for="item in sub.items"
							:key="`subitem-${item.ID}`"
							link
							class="drawer__item drawer__item--nested"
							:class="{ 'drawer__item--active': isItemActive(item) }"
							@click="pushRoute(item.link)"
						>
							<span class="drawer__item-indicator"></span>
							<v-icon size="16" class="drawer__item-icon">{{ item.icon }}</v-icon>
							<v-list-item-content>
								<v-list-item-title class="drawer__item-title">
									{{ item.title }}
								</v-list-item-title>
							</v-list-item-content>
							<v-btn
								icon
								x-small
								class="drawer__item-star"
								:class="{ 'drawer__item-star--on': isFavorite(item.ID) }"
								@click.stop="toggleFavorite(item)"
							>
								<v-icon size="16">
									{{ isFavorite(item.ID) ? "mdi-star" : "mdi-star-outline" }}
								</v-icon>
							</v-btn>
						</v-list-item>
					</v-list-group>
				</v-list-group>
			</template>
		</v-list>

		<template v-slot:append>
			<div v-if="visibleSettingsRoots.length > 0" class="drawer__settings">
				<div v-if="!getDrawer" class="drawer__settings-label">
					{{ $t("drawer.settings") }}
				</div>
				<v-list nav dense class="drawer__list drawer__list--settings">
					<template v-for="root in visibleSettingsRoots">
						<v-menu
							v-if="getDrawer"
							:key="`s-mini-${root.ID}`"
							open-on-hover
							offset-x
							right
							:close-delay="120"
							content-class="drawer__popout-menu"
							:disabled="!hasChildrenOrItems(root)"
						>
							<template v-slot:activator="{ on }">
								<v-list-item
									link
									class="drawer__mini-root"
									:class="{ 'drawer__mini-root--active': isRootActive(root) }"
									@click="navigateToGroup(root)"
									v-on="on"
								>
									<v-icon size="22">{{ root.icon }}</v-icon>
								</v-list-item>
							</template>

							<div class="drawer__popout">
								<div class="drawer__popout-header">
									<v-icon size="18" class="drawer__popout-header-icon">{{ root.icon }}</v-icon>
									<span class="drawer__popout-header-title">{{ root.title }}</span>
								</div>

								<div
									v-if="root.items && root.items.length > 0"
									class="drawer__popout-section"
								>
									<button
										v-for="item in root.items"
										:key="`s-popitem-${item.ID}`"
										type="button"
										class="drawer__popout-item"
										:class="{ 'drawer__popout-item--active': isItemActive(item) }"
										@click="pushRoute(item.link)"
									>
										<v-icon size="16">{{ item.icon }}</v-icon>
										<span>{{ item.title }}</span>
									</button>
								</div>

								<div
									v-for="sub in root.children"
									:key="`s-popsub-${sub.ID}`"
									class="drawer__popout-section"
								>
									<div class="drawer__popout-section-title">{{ sub.title }}</div>
									<button
										v-for="item in sub.items"
										:key="`s-popsubitem-${item.ID}`"
										type="button"
										class="drawer__popout-item"
										:class="{ 'drawer__popout-item--active': isItemActive(item) }"
										@click="pushRoute(item.link)"
									>
										<v-icon size="16">{{ item.icon }}</v-icon>
										<span>{{ item.title }}</span>
									</button>
								</div>
							</div>
						</v-menu>

						<v-list-group
							v-else
							:key="`s-grp-${root.ID}`"
							no-action
							:value="hasSearchQuery || isRootActive(root)"
							class="drawer__group"
						>
							<template v-slot:activator>
								<v-list-item-icon class="drawer__group-icon">
									<v-icon size="20">{{ root.icon }}</v-icon>
								</v-list-item-icon>
								<v-list-item-content>
									<v-list-item-title class="drawer__group-title">
										{{ root.title }}
									</v-list-item-title>
								</v-list-item-content>
							</template>

							<v-list-item
								v-for="item in root.items"
								:key="`s-item-${item.ID}`"
								link
								class="drawer__item"
								:class="{ 'drawer__item--active': isItemActive(item) }"
								@click="pushRoute(item.link)"
							>
								<span class="drawer__item-indicator"></span>
								<v-icon size="18" class="drawer__item-icon">{{ item.icon }}</v-icon>
								<v-list-item-content>
									<v-list-item-title class="drawer__item-title">
										{{ item.title }}
									</v-list-item-title>
								</v-list-item-content>
							</v-list-item>

							<v-list-group
								v-for="sub in root.children"
								:key="`s-sub-${sub.ID}`"
								no-action
								sub-group
								:value="hasSearchQuery || isSubGroupActive(sub)"
								class="drawer__subgroup"
							>
								<template v-slot:activator>
									<v-list-item-icon class="drawer__subgroup-icon">
										<v-icon size="18">{{ sub.icon }}</v-icon>
									</v-list-item-icon>
									<v-list-item-content>
										<v-list-item-title class="drawer__subgroup-title">
											{{ sub.title }}
										</v-list-item-title>
									</v-list-item-content>
								</template>

								<v-list-item
									v-for="item in sub.items"
									:key="`s-subitem-${item.ID}`"
									link
									class="drawer__item drawer__item--nested"
									:class="{ 'drawer__item--active': isItemActive(item) }"
									@click="pushRoute(item.link)"
								>
									<span class="drawer__item-indicator"></span>
									<v-icon size="16" class="drawer__item-icon">{{ item.icon }}</v-icon>
									<v-list-item-content>
										<v-list-item-title class="drawer__item-title">
											{{ item.title }}
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-list-group>
						</v-list-group>
					</template>
				</v-list>
			</div>

			<div class="drawer__footer">
				<v-btn
					v-if="docsUrl"
					icon
					class="drawer__docs"
					:href="docsUrl"
					target="_blank"
					rel="noopener noreferrer"
					:aria-label="$t('drawer.help')"
				>
					<v-icon size="22">mdi-book-open-variant</v-icon>
				</v-btn>
				<v-btn
					icon
					class="drawer__logout"
					:aria-label="$t('auth.logout')"
					@click="$auth.logout()"
				>
					<v-icon size="22">mdi-logout</v-icon>
				</v-btn>
			</div>
		</template>
	</v-navigation-drawer>
</template>

<script>
import { base } from "~/mixins/base.js";
import { getRuntimeConfig } from "../../utils/runtime-config";
import { isHttpUrl } from "../../utils/docs-link";

export default {
	mixins: [base],
	data() {
		return {
			menuSearch: "",
		};
	},
	computed: {
		// URL del sitio de documentación (opt-in por tenant). null si no está activada o
		// no es http(s) (guarda defensiva) ⇒ no se muestra el enlace "Documentación".
		docsUrl() {
			const cfg = getRuntimeConfig();
			const url = cfg && cfg.brand ? cfg.brand.docsUrl : undefined;
			return isHttpUrl(url) ? url : null;
		},
		// El sidebar está siempre colapsado (mini-rail). Sobrescribe el getter
		// del mixin base para forzar el estado sin tocar el store ni cada v-if.
		getDrawer: {
			get() {
				return true;
			},
			set() {},
		},
		brandLogo() {
			return "/brand/logo.png";
		},
		// Roots with order < 90 form the main navigation. The convention is
		// that "Ajustes" (and any future admin-only root) sits at order 99 and
		// is rendered separately at the bottom of the drawer.
		mainRoots() {
			return (this.getMenuTree || []).filter((r) => (r.order || 0) < 90);
		},
		settingsRoots() {
			return (this.getMenuTree || []).filter((r) => (r.order || 0) >= 90);
		},
		hasSearchQuery() {
			return !!(this.menuSearch && this.menuSearch.trim().length > 0);
		},
		favoriteForms() {
			return this.$store.getters["favorites/items"] || [];
		},
		visibleMainRoots() {
			return this.filterTree(this.mainRoots);
		},
		visibleSettingsRoots() {
			return this.filterTree(this.settingsRoots);
		},
	},
	methods: {
		isItemActive(item) {
			if (!item || !item.link) return false;
			const expected = "dashboard-" + item.link.replace(/\//g, "-");
			return this.$route.name === expected;
		},
		isRootActive(root) {
			const slug = this.slugifyMenuTitle(root.title);
			const path = this.$route.path || "";
			return path.startsWith(`/dashboard/${slug}`);
		},
		isSubGroupActive(sub) {
			if (!sub) return false;
			const subSlug = this.slugifyMenuTitle(sub.title);
			const path = this.$route.path || "";
			return path.includes(`/${subSlug}/`) || path.endsWith(`/${subSlug}`);
		},
		isFavorite(formID) {
			return this.$store.getters["favorites/isFavorite"](formID);
		},
		toggleFavorite(item) {
			if (!item) return;
			this.$store.dispatch("favorites/toggle", item);
		},
		hasChildrenOrItems(root) {
			if (!root) return false;
			const hasItems = (root.items || []).length > 0;
			const hasChildren = (root.children || []).length > 0;
			return hasItems || hasChildren;
		},
		normalizeForSearch(text) {
			if (!text) return "";
			return text
				.toString()
				.normalize("NFD")
				.replace(/[̀-ͯ]/g, "")
				.toLowerCase();
		},
		/**
		 * Returns the subset of `roots` whose own title, or any of whose
		 * descendant items/sub-groups, matches the current search query.
		 * Items and sub-groups inside each kept root are likewise filtered.
		 *
		 * If the query is empty, returns `roots` unchanged.
		 */
		filterTree(roots) {
			if (!this.hasSearchQuery) return roots;
			const q = this.normalizeForSearch(this.menuSearch.trim());
			const matches = (text) => this.normalizeForSearch(text).includes(q);

			return roots
				.map((root) => {
					const rootMatches = matches(root.title);
					const filteredItems = (root.items || []).filter((it) =>
						matches(it.title),
					);
					const filteredChildren = (root.children || [])
						.map((sub) => {
							const subMatches = matches(sub.title);
							const subItems = (sub.items || []).filter((it) =>
								matches(it.title),
							);
							if (!subMatches && subItems.length === 0) return null;
							return { ...sub, items: subMatches ? sub.items : subItems };
						})
						.filter(Boolean);

					if (
						!rootMatches &&
						filteredItems.length === 0 &&
						filteredChildren.length === 0
					) {
						return null;
					}
					return {
						...root,
						items: rootMatches ? root.items : filteredItems,
						children: rootMatches ? root.children : filteredChildren,
					};
				})
				.filter(Boolean);
		},
	},
};
</script>

<style lang="scss">
$drawer-bg: #ffffff;
$drawer-border: #eef0f3;
$drawer-text: #5a6473;
$drawer-text-strong: #1f2937;
// Accent del drawer = brand del tenant (vía design-system CSS vars).
// Antes era #104782 hardcoded → cuando el applier cambiaba la marca
// (commit dxxxx), el drawer seguía con el accent legacy.
$drawer-accent: var(--ui-accent, #104782);
$drawer-accent-soft: var(--ui-accent-soft, rgba(16, 71, 130, 0.08));
$drawer-hover: #f6f8fb;

#app-drawer {
	background-color: $drawer-bg !important;
	border-right: 1px solid $drawer-border !important;

	.drawer__brand {
		display: flex;
		align-items: center;
		justify-content: center;
		padding: 12px 4px;
		cursor: pointer;
		min-height: 64px;

		&-logo {
			flex: 0 0 40px !important;
			max-width: 40px !important;
		}

		&-name {
			font-size: 0.95rem;
			font-weight: 600;
			color: $drawer-text-strong;
			letter-spacing: -0.01em;
			white-space: nowrap;
		}
	}

	.drawer__divider {
		height: 1px;
		background: $drawer-border;
		margin: 0 12px;
	}

	.drawer__search {
		padding: 10px 12px 6px;
	}

	.drawer__search-input {
		border: 1px solid $drawer-border;
		border-radius: 10px;
		transition: border-color 0.15s ease, box-shadow 0.15s ease;

		&:focus-within {
			border-color: $drawer-accent;
			box-shadow: 0 0 0 3px var(--ui-accent-soft, rgba(16, 71, 130, 0.08));
		}

		.v-input__slot {
			background-color: transparent !important;
			padding-left: 8px !important;
			padding-right: 8px !important;
		}

		input {
			font-size: 0.875rem;
		}

		.v-icon {
			color: $drawer-text;
			font-size: 18px !important;
		}
	}

	.drawer__empty {
		padding: 12px 16px;
		font-size: 0.8125rem;
		color: $drawer-text;
		text-align: center;
	}

	.drawer__list {
		padding: 12px 4px !important;
	}

	.v-list-item__title {
		text-overflow: ellipsis;
		white-space: nowrap;
		overflow: hidden;
	}

	// Anula los paddings/margins horizontales que Vuetify aplica de
	// serie a v-icon (incluido el padding-right: 18px que viene en
	// .theme--light.v-icon en algunas versiones). Si no se anula, todos
	// los iconos del rail aparecen desplazados a la izquierda.
	.v-icon,
	.v-icon.v-icon,
	.theme--light.v-icon,
	.theme--dark.v-icon {
		padding-right: 0 !important;
		padding-left: 0 !important;
		margin-right: 0 !important;
		margin-left: 0 !important;
	}

	// Mini-rail (collapsed) root items
	.drawer__mini-root {
		display: flex !important;
		flex-direction: row !important;
		justify-content: center !important;
		align-items: center !important;
		padding: 0 !important;
		margin: 2px auto !important;
		width: calc(100% - 16px) !important;
		border-radius: 10px;
		min-height: 44px !important;
		color: $drawer-text;
		transition: background-color 0.15s ease, color 0.15s ease;

		&:hover {
			background-color: $drawer-hover;
			color: $drawer-text-strong;
		}

		&--active {
			background-color: $drawer-accent-soft;
			color: $drawer-accent;
		}

		// Fuerza a TODO hijo a comportarse como contenido neutro centrado.
		& > *,
		& > * > * {
			flex: 0 0 auto !important;
			margin: 0 !important;
			padding: 0 !important;
			min-width: 0 !important;
			max-width: none !important;
		}

		// Vuetify a veces envuelve children en .v-list-item__*; neutraliza todos.
		.v-list-item__action,
		.v-list-item__avatar,
		.v-list-item__content,
		.v-list-item__icon,
		.v-list-item__title,
		.v-list-item__subtitle {
			display: flex !important;
			justify-content: center !important;
			align-items: center !important;
		}

		.v-icon {
			color: inherit;
		}
	}

	// Expanded root group
	.drawer__group {
		margin-bottom: 2px;

		& > .v-list-group__header {
			border-radius: 10px;
			min-height: 40px !important;
			padding: 0 12px !important;
			transition: background-color 0.15s ease;

			&:hover {
				background-color: $drawer-hover;
			}
		}

		&-icon {
			margin-right: 12px !important;
			min-width: 22px !important;

			.v-icon {
				color: $drawer-text;
			}
		}

		&-title {
			font-size: 0.875rem;
			font-weight: 600;
			color: $drawer-text-strong;
			letter-spacing: -0.005em;
		}

		.v-list-group__header__append-icon {
			.v-icon {
				color: $drawer-text;
				font-size: 18px !important;
			}
		}
	}

	// Sub-group (nested)
	.drawer__subgroup {
		& > .v-list-group__header {
			border-radius: 8px;
			min-height: 36px !important;
			padding-left: 24px !important;
			transition: background-color 0.15s ease;

			&:hover {
				background-color: $drawer-hover;
			}
		}

		&-icon {
			margin-right: 10px !important;
			min-width: 18px !important;

			.v-icon {
				color: $drawer-text;
			}
		}

		&-title {
			font-size: 0.8125rem;
			font-weight: 500;
			color: $drawer-text;
		}
	}

	// Leaf item
	.drawer__item {
		position: relative;
		min-height: 36px !important;
		padding-left: 28px !important;
		padding-right: 12px !important;
		border-radius: 8px;
		margin: 1px 0;
		color: $drawer-text;
		transition: background-color 0.15s ease, color 0.15s ease;

		&--nested {
			padding-left: 44px !important;
		}

		&:hover {
			background-color: $drawer-hover;
			color: $drawer-text-strong;

			.drawer__item-icon {
				color: $drawer-text-strong;
			}
		}

		&-icon {
			color: $drawer-text;
			margin-right: 10px;
			transition: color 0.15s ease;
		}

		&-title {
			font-size: 0.8125rem;
			font-weight: 500;
			color: inherit;
		}

		&-indicator {
			position: absolute;
			left: 0;
			top: 50%;
			transform: translateY(-50%);
			width: 3px;
			height: 0;
			border-radius: 0 3px 3px 0;
			background-color: $drawer-accent;
			transition: height 0.18s ease;
		}

		&--active {
			background-color: $drawer-accent-soft !important;
			color: $drawer-accent !important;

			.drawer__item-icon,
			.drawer__item-title {
				color: $drawer-accent !important;
			}

			.drawer__item-indicator {
				height: 60%;
			}
		}
	}

	// Favorites block at the top (above the main nav).
	.drawer__favorites {
		padding-bottom: 4px;

		.drawer__divider {
			margin-top: 8px;
		}
	}

	.drawer__item-star {
		opacity: 0;
		color: $drawer-text !important;
		transition: opacity 0.15s ease, color 0.15s ease;

		.v-icon {
			color: inherit;
		}

		&--on {
			opacity: 1 !important;
			color: #f59e0b !important;
		}
	}

	.drawer__item:hover .drawer__item-star {
		opacity: 1;
	}

	// Settings block at the bottom (visually separated from main nav)
	.drawer__settings {
		border-top: 1px solid $drawer-border;
		padding: 8px 0 4px;
	}

	.drawer__settings-label {
		font-size: 0.6875rem;
		font-weight: 600;
		text-transform: uppercase;
		letter-spacing: 0.06em;
		color: lighten($drawer-text, 10%);
		padding: 6px 20px 4px;
	}

	.drawer__list--settings {
		padding-top: 0 !important;
		padding-bottom: 4px !important;
	}

	// Footer / logout
	.drawer__footer {
		padding: 10px 0;
		border-top: 1px solid $drawer-border;
		display: flex;
		flex-direction: column;
		align-items: center;
		gap: 4px;
	}

	.drawer__logout {
		color: $drawer-text !important;
		width: 44px !important;
		height: 44px !important;
		border-radius: 10px !important;
		transition: background-color 0.15s ease, color 0.15s ease;

		&:hover {
			background-color: $drawer-hover !important;
			color: $drawer-text-strong !important;
		}

		.v-icon {
			color: inherit;
		}
	}
}

// Smooth fade for brand name when toggling drawer
.drawer-fade-enter-active,
.drawer-fade-leave-active {
	transition: opacity 0.15s ease;
}
.drawer-fade-enter,
.drawer-fade-leave-to {
	opacity: 0;
}

// Popout content is rendered via Vuetify's v-menu, which teleports it
// outside #app-drawer. The styles below intentionally live at the top
// level so they reach the menu content via .drawer__popout-menu.
.drawer__popout-menu {
	background: #ffffff !important;
	border: 1px solid #eef0f3 !important;
	border-radius: 12px !important;
	box-shadow:
		0 10px 30px rgba(15, 23, 42, 0.08),
		0 2px 6px rgba(15, 23, 42, 0.05) !important;
	min-width: 240px;
	max-height: 70vh;
	overflow-y: auto;
	overflow-x: hidden;
}

.drawer__popout {
	padding: 8px 0;
}

.drawer__popout-header {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 6px 14px 10px;
	border-bottom: 1px solid #eef0f3;
	margin-bottom: 4px;

	.drawer__popout-header-icon {
		color: var(--ui-accent, #104782);
	}

	.drawer__popout-header-title {
		font-size: 0.875rem;
		font-weight: 600;
		color: #1f2937;
		letter-spacing: -0.005em;
	}
}

.drawer__popout-section {
	padding: 4px 6px;

	& + .drawer__popout-section {
		border-top: 1px solid #f5f7fa;
		margin-top: 4px;
		padding-top: 8px;
	}
}

.drawer__popout-section-title {
	font-size: 0.6875rem;
	font-weight: 600;
	color: #94a3b8;
	text-transform: uppercase;
	letter-spacing: 0.06em;
	padding: 4px 10px 6px;
}

.drawer__popout-item {
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	padding: 8px 12px;
	border: 0;
	background: transparent;
	cursor: pointer;
	text-align: left;
	color: #5a6473;
	border-radius: 8px;
	transition:
		background-color 0.12s ease,
		color 0.12s ease;

	&:hover {
		background-color: #f6f8fb;
		color: #1f2937;

		.v-icon {
			color: #1f2937;
		}
	}

	&--active {
		background-color: var(--ui-accent-soft, rgba(16, 71, 130, 0.08));
		color: var(--ui-accent, #104782);

		.v-icon {
			color: var(--ui-accent, #104782);
		}
	}

	.v-icon {
		color: inherit;
	}

	span {
		font-size: 0.8125rem;
		font-weight: 500;
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}
}
</style>
